import { useEffect, useRef } from 'react';
import { useUser, useAuth as useClerkAuth } from '@clerk/clerk-react';
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';

/**
 * usePushRegistration
 *
 * Native iOS only. Registers the Capacitor push-notification listeners and posts
 * the resulting APNs device token to /api/push/register-device with the user's
 * Clerk Bearer token attached.
 *
 * Why this exists:
 *   The legacy bootstrap path in `index.js` ran the registration POST BEFORE
 *   React mounted, which meant (a) Clerk had no session yet to mint a token from
 *   and (b) the request went out with no Authorization header, so the backend
 *   returned 401 and the device token never landed in `device_tokens`.
 *
 *   This hook fixes both problems by:
 *     - Waiting until Clerk reports `isLoaded && isSignedIn`
 *     - Calling `getToken()` to get a fresh Clerk session JWT
 *     - Sending `Authorization: Bearer ${token}` on every register-device POST
 *
 * Idempotent: registers exactly once per session via a ref guard. The Capacitor
 * `registration` listener may fire on every app foreground — we always re-POST
 * to keep the backend up-to-date, but we never double-attach listeners.
 *
 * Logging is intentionally verbose so the WKWebView console makes the next
 * debugging round trivial.
 */
export default function usePushRegistration() {
  const { isLoaded, isSignedIn } = useUser();
  const { getToken } = useClerkAuth();
  const registeredRef = useRef(false);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    if (!isLoaded || !isSignedIn) return;
    if (registeredRef.current) return;
    registeredRef.current = true;

    let cancelled = false;

    const register = async () => {
      try {
        const permission = await PushNotifications.requestPermissions();
        if (cancelled) return;

        if (permission.receive !== 'granted') {
          console.log('[push] permission not granted:', permission.receive);
          return;
        }

        // Listener: APNs returned a token. Send it to the backend with the
        // current Clerk Bearer token. Re-fires every app foreground; we always
        // re-POST so the backend has a fresh updated_at + the latest token.
        PushNotifications.addListener('registration', async (token) => {
          console.log('[push] APNs token received (prefix):', token.value?.slice(0, 8));
          try {
            const clerkToken = (await getToken()) || '';
            if (!clerkToken) {
              console.warn('[push] No Clerk token available — skipping register-device POST');
              return;
            }

            const res = await fetch('https://ineednumbers.com/api/push/register-device', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${clerkToken}`,
              },
              credentials: 'include',
              body: JSON.stringify({ device_token: token.value }),
            });

            if (res.ok) {
              console.log('[push] register-device OK', res.status);
            } else {
              const body = await res.text().catch(() => '');
              console.error('[push] register-device FAILED', res.status, body.slice(0, 300));
            }
          } catch (err) {
            console.error('[push] register-device threw:', err?.message || err);
          }
        });

        PushNotifications.addListener('registrationError', (err) => {
          console.error('[push] APNs registrationError:', err);
        });

        PushNotifications.addListener('pushNotificationReceived', (notification) => {
          console.log('[push] notification received in foreground:', notification);
        });

        PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
          console.log('[push] notification tapped:', action);
        });

        await PushNotifications.register();
        console.log('[push] PushNotifications.register() called — waiting for APNs token');
      } catch (err) {
        console.error('[push] setup error:', err?.message || err);
      }
    };

    register();

    return () => {
      cancelled = true;
      // Capacitor listeners persist for the app's lifetime — we only guard
      // against double-attach via registeredRef. Removing them on unmount
      // would mean the listener stops firing if the React tree re-mounts,
      // which is never desirable here.
    };
  }, [isLoaded, isSignedIn, getToken]);
}
